import { CANVAS_LIST_QUERY_KEYS } from "@/api/canvasKeys";
import { fileRecordKeys } from "@/api/fileRecords";
import { config } from "@/config";
import {
  Canvas,
  CanvasPublishResponse,
  CreateCanvasInWorkspaceRequest,
  FileRecord,
  FlowSession,
  UpdateCanvasRequest,
  Workspace,
} from "@cohesive/api-client";
import {
  type QueryClient,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { useCallback } from "react";
import { orgAPI } from "./api";
import { invalidateArchivedQueries } from "./archived";
import { usePrivateWorkspace, useWorkspaces } from "./workspaces";

/**
 * Default name for a freshly-created canvas. This MUST stay in sync with the
 * backend auto-name guard (`canvas_name_generate.go`), which only replaces a
 * canvas name when it still equals this exact literal. If the two drift, new
 * canvases never get an AI-generated title.
 */
export const DEFAULT_CANVAS_NAME = "Untitled";

/**
 * Optimistically updates `hero_generating` on cached canvas detail and list entries.
 */
export function setCanvasHeroGenerating(
  queryClient: QueryClient,
  canvasId: string,
  hero_generating: boolean,
) {
  queryClient.setQueryData<Canvas>(["canvas", canvasId], (old) => {
    if (!old) return old;
    return {
      ...old,
      metadata: { ...old.metadata, hero_generating },
    };
  });
  queryClient.setQueryData<Canvas[]>(["canvases"], (old) => {
    if (!old) return old;
    return old.map((c) =>
      String(c.id) === canvasId
        ? { ...c, metadata: { ...c.metadata, hero_generating } }
        : c,
    );
  });
}

/**
 * Patch publish fields on cached canvas detail + every canvases list query.
 */
function patchCanvasPublishFields(
  queryClient: QueryClient,
  canvasId: string,
  patch: Pick<Canvas, "published_status" | "published_at">,
) {
  queryClient.setQueryData<Canvas>(["canvas", canvasId], (old) => {
    if (!old) return old;
    return { ...old, ...patch };
  });
  for (const [key, list] of queryClient.getQueriesData<Canvas[]>({
    queryKey: ["canvases"],
  })) {
    if (!Array.isArray(list)) continue;
    queryClient.setQueryData<Canvas[]>(key, (old) => {
      if (!old) return old;
      return old.map((c) =>
        String(c.id) === canvasId ? { ...c, ...patch } : c,
      );
    });
  }
}

const PUBLISH_POLL_INTERVAL_MS = 750;
const PUBLISH_POLL_TIMEOUT_MS = 90_000;

/** In-flight publish poll AbortControllers, keyed by canvas id. */
const publishPollControllers = new Map<string, AbortController>();

type CanvasPublishPollBaseline = {
  published_status: string;
  published_at?: string;
  // updated_at distinguishes a no-op republish restore (same published_at,
  // bumped updated_at) from a lagging GET of the pre-job row.
  updated_at?: string;
};

function abortPublishPoll(canvasId: string) {
  const existing = publishPollControllers.get(canvasId);
  if (!existing) return;
  existing.abort();
  publishPollControllers.delete(canvasId);
}

/**
 * True when a GET still mirrors the pre-publish row. The publish POST returns
 * 202 before the workflow writes `publishing`, so early polls can still see
 * `""`, prior `published`, or prior `failed`.
 *
 * No-op republish restores `published` with the same `published_at`; without
 * `updated_at` that terminal row looks identical to the baseline and the poll
 * would ignore a successful job forever when the WS event is missed.
 */
export function isStaleCanvasPublishPollSnapshot(
  canvas: Pick<Canvas, "published_status" | "published_at" | "updated_at">,
  baseline: CanvasPublishPollBaseline,
): boolean {
  const status = canvas.published_status ?? "";
  return (
    status === baseline.published_status &&
    (canvas.published_at ?? undefined) ===
      (baseline.published_at ?? undefined) &&
    (canvas.updated_at ?? undefined) === (baseline.updated_at ?? undefined)
  );
}

/**
 * Poll GET canvas until `published_status` leaves `publishing` (or timeout).
 * Safety net when the WS `canvas.updated` event is missed or delayed.
 *
 * Ignores GETs that still match `baseline` (the row before this publish) so a
 * lagging response cannot overwrite optimistic `publishing` or end the poll
 * before the job is visible. Accepts a fast `published`/`failed` that diverges
 * from baseline even if we never observed the intermediate `publishing` tick.
 * Aborts when a newer publish poll starts or the canvas is unpublished.
 */
async function pollCanvasPublishTerminal(
  queryClient: QueryClient,
  canvasId: string,
  signal: AbortSignal,
  baseline: CanvasPublishPollBaseline,
): Promise<void> {
  const deadline = Date.now() + PUBLISH_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    if (signal.aborted) return;
    try {
      const canvas = await orgAPI.canvas.getById(canvasId);
      if (signal.aborted) return;

      if (!isStaleCanvasPublishPollSnapshot(canvas, baseline)) {
        const status = canvas.published_status ?? "";
        patchCanvasPublishFields(queryClient, canvasId, {
          published_status: status,
          published_at: canvas.published_at,
        });
        if (status !== "publishing") {
          return;
        }
      }
      // else: still the pre-job snapshot — keep optimistic publishing
    } catch {
      // Keep polling through transient errors.
    }
    if (signal.aborted) return;
    await new Promise((resolve) =>
      setTimeout(resolve, PUBLISH_POLL_INTERVAL_MS),
    );
  }

  if (signal.aborted) return;

  // Avoid leaving the Share UI stuck on optimistic "publishing" forever when
  // WS + poll both miss a terminal status — refetch from the server.
  await queryClient.invalidateQueries({ queryKey: ["canvas", canvasId] });
  await queryClient.invalidateQueries({ queryKey: ["canvases"] });
}

function startCanvasPublishPoll(
  queryClient: QueryClient,
  canvasId: string,
  baseline: CanvasPublishPollBaseline,
) {
  abortPublishPoll(canvasId);
  const controller = new AbortController();
  publishPollControllers.set(canvasId, controller);
  void pollCanvasPublishTerminal(
    queryClient,
    canvasId,
    controller.signal,
    baseline,
  ).finally(() => {
    if (publishPollControllers.get(canvasId) === controller) {
      publishPollControllers.delete(canvasId);
    }
  });
}

function isIPv4Hostname(hostname: string): boolean {
  const parts = hostname.split(".");
  if (parts.length !== 4) return false;
  return parts.every((part) => {
    if (!/^\d{1,3}$/.test(part)) return false;
    const n = Number(part);
    return n >= 0 && n <= 255;
  });
}

/**
 * For server-side capture, prefer an explicit capture host when configured
 * (`VITE_CAPTURE_BASE_URL`). PR previews must use a stable host (app.dev) —
 * the screenshot lambda loads `{origin}/canvas-capture.html`, and capturing
 * against ephemeral `app-pr-*` origins is unreliable.
 *
 * When unset: use the current origin on a real hostname; on localhost / raw IP,
 * fall back to `VITE_APP_BASE_URL` then origin.
 */
function resolveCanvasScreenshotAppBaseUrl(): string {
  const configuredCapture = (config.hosts.capture || "").replace(/\/$/, "");
  if (configuredCapture) {
    return configuredCapture;
  }

  const origin = window.location.origin;
  const hostname = window.location.hostname;
  const isLocalhostOrIp =
    hostname.toLowerCase() === "localhost" ||
    isIPv4Hostname(hostname) ||
    hostname.includes(":");
  if (!isLocalhostOrIp) {
    return origin;
  }
  return (config.hosts.app || origin).replace(/\/$/, "");
}

/**
 * Public share URL for a published canvas. Host comes from `config.hosts.share`
 * (`VITE_SHARE_BASE_URL`; empty falls back to `window.location.origin`); path is
 * `/view/canvas/{id}`.
 */
export function buildCanvasPublicUrl(canvasId: string): string {
  const base = (config.hosts.share || window.location.origin).replace(
    /\/$/,
    "",
  );
  return `${base}/view/canvas/${encodeURIComponent(canvasId)}`;
}

/**
 * Enqueues server-side preview regeneration (`POST .../generate-preview`) and toggles
 * `metadata.hero_generating` in the React Query cache. Swallows HTTP 501 (feature off).
 */
export function useCreateCanvasScreenshot(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: async (canvasId: string) => {
      if (!canvasId) return;
      try {
        await orgAPI.canvas.generatePreview(canvasId, {
          appBaseUrl: resolveCanvasScreenshotAppBaseUrl(),
        });
        setCanvasHeroGenerating(queryClient, canvasId, true);
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 501) {
          return;
        }
        setCanvasHeroGenerating(queryClient, canvasId, false);
      }
    },
  });
}

/**
 * Kick off async public-canvas publish. Optimistically sets `published_status`
 * to `publishing`; terminal status arrives via `canvas.updated` (with GET poll
 * fallback).
 */
type CanvasPublishMutationContext = {
  previousDetail?: Canvas;
};

export function usePublishCanvas(
  options?: UseMutationOptions<
    CanvasPublishResponse,
    AxiosError,
    string,
    CanvasPublishMutationContext
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (canvasId: string) =>
      orgAPI.canvas.publish(canvasId, {
        appBaseUrl: resolveCanvasScreenshotAppBaseUrl(),
      }),
    ...options,
    onMutate: async (canvasId, ...rest) => {
      abortPublishPoll(canvasId);
      await queryClient.cancelQueries({ queryKey: ["canvas", canvasId] });
      const previousDetail = queryClient.getQueryData<Canvas>([
        "canvas",
        canvasId,
      ]);
      patchCanvasPublishFields(queryClient, canvasId, {
        published_status: "publishing",
      });
      const fromOptions = await options?.onMutate?.(canvasId, ...rest);
      return { previousDetail, ...fromOptions };
    },
    onError: (error, canvasId, context, ...rest) => {
      if (context?.previousDetail) {
        // Restore detail + every canvases list entry (onMutate patched both).
        patchCanvasPublishFields(queryClient, canvasId, {
          published_status: context.previousDetail.published_status ?? "",
          published_at: context.previousDetail.published_at,
        });
        queryClient.setQueryData(["canvas", canvasId], context.previousDetail);
      }
      options?.onError?.(error, canvasId, context, ...rest);
    },
    onSuccess: async (data, canvasId, context, ...rest) => {
      startCanvasPublishPoll(queryClient, canvasId, {
        published_status: context?.previousDetail?.published_status ?? "",
        published_at: context?.previousDetail?.published_at,
        updated_at: context?.previousDetail?.updated_at,
      });
      await options?.onSuccess?.(data, canvasId, context, ...rest);
    },
  });
}

/**
 * Copy a published canvas into one of the caller's workspaces (viewer
 * "Add to my workspace"). 202 returns the new canvas shell; hydration progress
 * is polled via `pollImportedCanvasTerminal` (wait-then-redirect UX). No cache
 * work: the viewer holds no canvas lists, and app lists refresh via the
 * existing `canvas.created` WS invalidation.
 */
export function useCopyPublishedCanvas(
  options?: UseMutationOptions<
    Canvas,
    AxiosError,
    { canvasId: string; workspaceId?: string }
  >,
) {
  return useMutation({
    mutationFn: ({ canvasId, workspaceId }) =>
      orgAPI.canvas.copyPublished(
        canvasId,
        workspaceId ? { workspace_id: workspaceId } : undefined,
      ),
    ...options,
  });
}

/**
 * Revoke the public canvas snapshot. Optimistically clears publish fields.
 */
export function useUnpublishCanvas(
  options?: UseMutationOptions<
    void,
    AxiosError,
    string,
    CanvasPublishMutationContext
  >,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (canvasId: string) => orgAPI.canvas.unpublish(canvasId),
    ...options,
    onMutate: async (canvasId, ...rest) => {
      abortPublishPoll(canvasId);
      await queryClient.cancelQueries({ queryKey: ["canvas", canvasId] });
      const previousDetail = queryClient.getQueryData<Canvas>([
        "canvas",
        canvasId,
      ]);
      patchCanvasPublishFields(queryClient, canvasId, {
        published_status: "",
        published_at: undefined,
      });
      const fromOptions = await options?.onMutate?.(canvasId, ...rest);
      return { previousDetail, ...fromOptions };
    },
    onError: (error, canvasId, context, ...rest) => {
      if (context?.previousDetail) {
        patchCanvasPublishFields(queryClient, canvasId, {
          published_status: context.previousDetail.published_status ?? "",
          published_at: context.previousDetail.published_at,
        });
        queryClient.setQueryData(["canvas", canvasId], context.previousDetail);
      }
      options?.onError?.(error, canvasId, context, ...rest);
    },
    onSuccess: (...args) => {
      const [, canvasId] = args;
      abortPublishPoll(canvasId);
      // Ensure detail cache reflects cleared publish state even if onMutate
      // raced a WS event that re-set publishing.
      patchCanvasPublishFields(queryClient, canvasId, {
        published_status: "",
        published_at: undefined,
      });
      options?.onSuccess?.(...args);
    },
  });
}

export function useCanvases(
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvases", "all"],
    queryFn: async () => {
      const workspaces = await orgAPI.workspaces
        .list()
        .then((workspaces) =>
          workspaces.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          ),
        );

      const fetches: Promise<Canvas[]>[] = [
        // Directly shared canvases
        orgAPI.canvas.listSharedWithMe().catch(() => []),
        // All workspace canvases in parallel
        ...(workspaces ?? []).map((ws) =>
          orgAPI.workspaces.listCanvases(ws.id).catch(() => []),
        ),
      ];

      const results = await Promise.all(fetches);
      const all = results.flat();

      // Deduplicate by canvas ID (a canvas could appear in both a workspace
      // and shared-with-me if pruning hasn't run yet).
      const seen = new Set<string>();
      return all
        .filter((c) => {
          if (seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        })
        .sort(
          (a, b) =>
            new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
        );
    },
    enabled: options?.enabled ?? true,
    ...options,
  });
}

export function useSharedWithMeCanvases(
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvas", "sharedWithMe"],
    queryFn: () => orgAPI.canvas.listSharedWithMe(),
    ...options,
  });
}

/**
 * Recent canvases the user can access, ordered most-recently-modified first.
 * Backed by `GET .../canvases?limit=`. The `["canvases", ...]` key prefix means
 * canvas mutations (create/update/delete/duplicate) already invalidate this.
 */
export function useRecentCanvases(
  limit = 8,
  options?: Partial<UseQueryOptions<Canvas[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvases", "recent", limit],
    queryFn: () => orgAPI.canvas.list({ limit }),
    ...options,
  });
}

export interface WorkspaceSidebarData {
  workspaces: Workspace[];
  sharedWithMe: Canvas[];
}

export function useWorkspaceSidebarData() {
  const {
    data: workspaces,
    isPending: workspacesPending,
    isError: workspacesError,
  } = useWorkspaces();

  const sharedQuery = useSharedWithMeCanvases();

  const isPending = workspacesPending || sharedQuery.isPending;
  const isError = workspacesError || sharedQuery.isError;

  const data: WorkspaceSidebarData | undefined =
    workspaces && sharedQuery.data
      ? {
          workspaces: workspaces ?? [],
          sharedWithMe: sharedQuery.data ?? [],
        }
      : undefined;

  return { data, isPending, isError };
}

export function useCanvasById(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<Canvas, AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvas", canvasId],
    queryFn: () => {
      if (!canvasId) {
        throw new Error("Canvas ID is required");
      }
      return orgAPI.canvas.getById(canvasId);
    },
    enabled: !!canvasId && (options?.enabled ?? true),
    ...options,
  });
}

export type CreateCanvasVariables = Omit<
  CreateCanvasInWorkspaceRequest,
  "name"
> & {
  /** Canvas name. Defaults to {@link DEFAULT_CANVAS_NAME} when omitted/blank. */
  name?: string;
  /** Workspace to create in. Defaults to the current user's private workspace. */
  workspaceId?: string;
  /** Folder in the workspace tree to create it in ("" = the workspace root). */
  parent_path?: string;
};

const RECENT_CANVASES_BY_WORKSPACE_KEY = [
  "workspaces",
  "recent-canvases-by-workspace",
] as const;

/**
 * Cached `Canvas[]` lists holding rows a canvas mutation has to keep current:
 * the recent / all lists, shared-with-me, the per-workspace lists and — for
 * content updates only — the favorites list.
 *
 * Maintenance rule: any new query that caches a `Canvas[]` has to be
 * registered here. Nothing prefix-invalidates canvas lists any more, so a list
 * this function does not return is a list that never sees a rename, a move or
 * a delete. The recent-by-workspace map is the one exception: it is keyed by
 * workspace rather than being a list, and mutations invalidate it wholesale.
 *
 * `["canvases", "favorite"]` is membership-by-star, so it is offered only when
 * `forContentUpdate` is set: insert and remove must not add or drop a row
 * there, but a rename or a move still has to reach the rail's Favorites
 * section without waiting for an event.
 */
function cachedCanvasListKeys(
  queryClient: QueryClient,
  { forContentUpdate = false } = {},
): unknown[][] {
  const keys: unknown[][] = [];
  for (const [key, list] of queryClient.getQueriesData<Canvas[]>({
    queryKey: ["canvases"],
  })) {
    if (!Array.isArray(list)) continue;
    if (
      key[1] === "recent" ||
      key[1] === "all" ||
      (forContentUpdate && key[1] === "favorite")
    ) {
      keys.push([...key]);
    }
  }
  for (const [key, list] of queryClient.getQueriesData<Canvas[]>({
    queryKey: ["canvas", "sharedWithMe"],
  })) {
    if (Array.isArray(list)) keys.push([...key]);
  }
  for (const [key, list] of queryClient.getQueriesData<Canvas[]>({
    queryKey: ["workspaces"],
  })) {
    if (Array.isArray(list) && key[2] === "canvases") keys.push([...key]);
  }
  return keys;
}

/**
 * Narrows the cached lists a splice or a removal touches. A move only changes
 * workspace membership: the workspace-agnostic lists (`recent`, `all`,
 * shared-with-me) keep the row and are patched in place instead.
 */
interface CachedListScope {
  /** Only `["workspaces", id, "canvases"]`. */
  workspaceListsOnly?: boolean;
  /** Leave this workspace's list alone. */
  exceptWorkspaceId?: string;
}

function isOutOfScope(key: unknown[], scope?: CachedListScope): boolean {
  if (scope?.workspaceListsOnly && key[0] !== "workspaces") return true;
  if (
    scope?.exceptWorkspaceId !== undefined &&
    key[0] === "workspaces" &&
    key[1] === scope.exceptWorkspaceId
  ) {
    return true;
  }
  return false;
}

/**
 * Splices a canvas into the lists it belongs to. Every one of them is ordered
 * most-recently-modified first, so the row goes on top; `["canvases",
 * "recent", limit]` is re-trimmed to its limit.
 *
 * Shared-with-me and other workspaces' lists are skipped: a canvas that lives
 * in one workspace is in neither.
 */
export function insertCanvasIntoCachedLists(
  queryClient: QueryClient,
  canvas: Canvas,
  scope?: CachedListScope,
): void {
  const workspaceId = canvas.workspace_id
    ? String(canvas.workspace_id)
    : undefined;
  for (const key of cachedCanvasListKeys(queryClient)) {
    if (isOutOfScope(key, scope)) continue;
    if (key[0] === "canvas") continue;
    if (key[0] === "workspaces" && key[1] !== workspaceId) continue;
    queryClient.setQueryData<Canvas[]>(key, (old) => {
      if (!old) return old;
      if (old.some((c) => String(c.id) === String(canvas.id))) return old;
      const next = [canvas, ...old];
      const limit = key[1] === "recent" ? key[2] : undefined;
      return typeof limit === "number" ? next.slice(0, limit) : next;
    });
  }
}

/**
 * Replaces the canvas's row in every cached list that already holds it. The
 * event path passes its own merge: an event payload is a slim projection and
 * overwriting a full row with it would drop fields.
 */
export function patchCanvasInCachedLists(
  queryClient: QueryClient,
  canvas: Canvas,
  merge: (previous: Canvas, next: Canvas) => Canvas = (previous, next) => ({
    ...previous,
    ...next,
  }),
): void {
  for (const key of cachedCanvasListKeys(queryClient, {
    forContentUpdate: true,
  })) {
    queryClient.setQueryData<Canvas[]>(key, (old) => {
      if (!old?.some((c) => String(c.id) === String(canvas.id))) return old;
      return old.map((c) =>
        String(c.id) === String(canvas.id) ? merge(c, canvas) : c,
      );
    });
  }
}

/** Drops the canvas's row from every cached list in scope. */
function removeCanvasFromCachedLists(
  queryClient: QueryClient,
  canvasId: string,
  scope?: CachedListScope,
): void {
  for (const key of cachedCanvasListKeys(queryClient)) {
    if (isOutOfScope(key, scope)) continue;
    queryClient.setQueryData<Canvas[]>(key, (old) => {
      if (!old?.some((c) => String(c.id) === canvasId)) return old;
      return old.filter((c) => String(c.id) !== canvasId);
    });
  }
}

/**
 * Files the canvas under its current workspace in the workspace-scoped lists,
 * dropping it from every other one. Patching in place is not enough: after a
 * move the row would stay in the source workspace's list and never appear in
 * the target's, which is what the home canvases tab and the destination picker
 * read.
 *
 * Called on every update rather than only on a detected move. Whether a move
 * happened can only be inferred from caches this client happens to hold, and a
 * client holding just the lists has nothing to infer from; the write is
 * in-memory and idempotent, so a non-move is a no-op.
 */
export function rehomeCanvasInCachedWorkspaceLists(
  queryClient: QueryClient,
  canvas: Canvas,
): void {
  // Without a workspace to file it under there is no re-homing to do, and
  // removing it from every list would just lose the row.
  if (!canvas.workspace_id) return;
  const canvasId = String(canvas.id);
  // Gated on a row this client already holds, anywhere. Updates are broadcast
  // org-wide, and filing one for a canvas the client has never seen would put
  // a row it cannot open into the list; a row in any cache is proof it can.
  // The source workspace's list need not be cached for this — recent, all,
  // favorites, shared-with-me or an open tab's detail entry all count. Every
  // cache has been patched by the time this runs, so the held row is also the
  // freshest content to file, where the event payload is only a projection.
  const held = cachedCanvasRow(queryClient, canvasId);
  if (!held) return;

  removeCanvasFromCachedLists(queryClient, canvasId, {
    workspaceListsOnly: true,
    exceptWorkspaceId: String(canvas.workspace_id),
  });
  insertCanvasIntoCachedLists(
    queryClient,
    { ...held, workspace_id: canvas.workspace_id },
    { workspaceListsOnly: true },
  );
}

/**
 * The row the caches hold for this canvas, if any: the detail entry, then any
 * registered list, then a tree row.
 */
export function cachedCanvasRow(
  queryClient: QueryClient,
  canvasId: string,
): Canvas | undefined {
  const detail = queryClient.getQueryData<Canvas>(["canvas", canvasId]);
  if (detail) return detail;
  for (const key of cachedCanvasListKeys(queryClient, {
    forContentUpdate: true,
  })) {
    const row = queryClient
      .getQueryData<Canvas[]>(key)
      ?.find((c) => String(c.id) === canvasId);
    if (row) return row;
  }
  for (const [, records] of queryClient.getQueriesData<FileRecord[]>({
    queryKey: fileRecordKeys.workspaces,
  })) {
    if (!Array.isArray(records)) continue;
    const row = records.find(
      (r) => r.canvas && String(r.canvas.id) === canvasId,
    )?.canvas;
    if (row) return row;
  }
  return undefined;
}

/**
 * Where the caches currently believe this canvas lives, read before an update
 * is applied. Both the workspace trees and the per-workspace lists count: a
 * client can hold either without the other, and a tree that is cached but has
 * no row for the canvas still has to be refetched when the canvas moves in.
 */
export function cachedCanvasPlacement(
  queryClient: QueryClient,
  canvasId: string,
): { workspaceIds: string[]; name?: string } {
  const workspaceIds = new Set<string>();
  let name: string | undefined;
  for (const [key, records] of queryClient.getQueriesData<FileRecord[]>({
    queryKey: fileRecordKeys.workspaces,
  })) {
    const workspaceId = key[2];
    if (!Array.isArray(records) || typeof workspaceId !== "string") continue;
    const row = records.find(
      (r) => r.canvas && String(r.canvas.id) === canvasId,
    );
    if (!row?.canvas) continue;
    workspaceIds.add(workspaceId);
    name ??= row.canvas.name;
  }
  for (const [key, list] of queryClient.getQueriesData<Canvas[]>({
    queryKey: ["workspaces"],
  })) {
    if (key[2] !== "canvases" || !Array.isArray(list)) continue;
    const row = list.find((c) => String(c.id) === canvasId);
    if (!row || typeof key[1] !== "string") continue;
    workspaceIds.add(key[1]);
    name ??= row.name;
  }
  return { workspaceIds: [...workspaceIds], name };
}

/**
 * A tree row embeds the canvas it points at (name, hero, star), so a canvas
 * change has to reach `record.canvas` or the rail keeps showing the old row
 * until its tree is fetched again — and a tree fetch presigns every row.
 */
export function patchCanvasInCachedTrees(
  queryClient: QueryClient,
  canvasId: string,
  merge: (previous: Canvas) => Canvas,
): void {
  for (const [key, records] of queryClient.getQueriesData<FileRecord[]>({
    queryKey: fileRecordKeys.workspaces,
  })) {
    if (!Array.isArray(records)) continue;
    if (!records.some((r) => r.canvas && String(r.canvas.id) === canvasId)) {
      continue;
    }
    queryClient.setQueryData<FileRecord[]>(key, (old) =>
      old?.map((r) =>
        r.canvas && String(r.canvas.id) === canvasId
          ? { ...r, canvas: merge(r.canvas) }
          : r,
      ),
    );
  }
}

/**
 * Every cached list a canvas row can appear in. Detail entries are left alone
 * on purpose: a list change says nothing about `["canvas", id]`, and every
 * open tab holds one.
 */
export function invalidateCanvasListQueries(queryClient: QueryClient): void {
  for (const queryKey of CANVAS_LIST_QUERY_KEYS) {
    queryClient.invalidateQueries({ queryKey });
  }
  queryClient.invalidateQueries({ queryKey: RECENT_CANVASES_BY_WORKSPACE_KEY });
  queryClient.invalidateQueries({
    predicate: (query) =>
      query.queryKey[0] === "workspaces" && query.queryKey[2] === "canvases",
  });
}

/**
 * Canvas ids this runtime's create mutation has already written into the
 * cache. The `canvas.created` event that follows would only redo the same
 * work — and cancel the fetches the mutation avoided — so the originating
 * runtime consumes its own id once. Per runtime, not per user: the same
 * user's other tabs have caches of their own and still need the event. The
 * TTL keeps a dropped event from leaving an entry behind that would silence a
 * genuine later event for the same id.
 */
const LOCAL_CANVAS_CREATE_TTL_MS = 30_000;
const locallyCreatedCanvasIds = new Map<string, number>();

/**
 * Create requests this runtime has in flight, by workspace, as expiry stamps.
 * The server publishes `canvas.created` before it writes the response, so the
 * event can reach this runtime before the id above is recorded; the workspace
 * a create is outstanding for is what identifies it in that window. Expiries
 * rather than a count so a mutation that never settles can't silence events
 * for its workspace forever.
 */
const inFlightCanvasCreates = new Map<string, number[]>();

/**
 * Ids whose `canvas.created` was already answered by the in-flight match, so
 * the create's own `markCanvasCreatedLocally` doesn't leave behind an entry
 * that would silence a genuine later event for the same id.
 */
const spentCanvasCreateEvents = new Map<string, number>();

function pruneExpired(entries: Map<string, number>): void {
  const now = Date.now();
  for (const [key, expiresAt] of entries) {
    if (expiresAt <= now) entries.delete(key);
  }
}

function markCanvasCreatedLocally(canvasId: string): void {
  pruneExpired(locallyCreatedCanvasIds);
  pruneExpired(spentCanvasCreateEvents);
  if (spentCanvasCreateEvents.delete(canvasId)) return;
  locallyCreatedCanvasIds.set(
    canvasId,
    Date.now() + LOCAL_CANVAS_CREATE_TTL_MS,
  );
}

function beginLocalCanvasCreate(workspaceId: string): void {
  const pending = inFlightCanvasCreates.get(workspaceId) ?? [];
  pending.push(Date.now() + LOCAL_CANVAS_CREATE_TTL_MS);
  inFlightCanvasCreates.set(workspaceId, pending);
}

function endLocalCanvasCreate(workspaceId: string): void {
  const pending = inFlightCanvasCreates.get(workspaceId);
  if (!pending) return;
  pending.shift();
  if (pending.length === 0) inFlightCanvasCreates.delete(workspaceId);
}

function hasLocalCanvasCreateInFlight(workspaceId: string): boolean {
  const now = Date.now();
  const pending = inFlightCanvasCreates
    .get(workspaceId)
    ?.filter((expiresAt) => expiresAt > now);
  if (!pending?.length) {
    inFlightCanvasCreates.delete(workspaceId);
    return false;
  }
  inFlightCanvasCreates.set(workspaceId, pending);
  return true;
}

/**
 * True when this runtime's own create produced the canvas the event describes,
 * so the event handler can leave every cache alone. Two orderings reach here:
 * the mutation resolved first and recorded the id, or the event overtook the
 * response and is matched by an outstanding create for the same workspace by
 * the same user. The second is a heuristic — the same user creating in two
 * tabs of the same workspace in the same instant would suppress one tab's
 * event — and it costs that tab one stale rail row until its next tree fetch.
 */
export function consumeLocalCanvasCreate(
  canvasId: string,
  workspaceId?: string,
  isCurrentUser = false,
): boolean {
  const expiresAt = locallyCreatedCanvasIds.get(canvasId);
  if (expiresAt !== undefined) {
    locallyCreatedCanvasIds.delete(canvasId);
    if (expiresAt > Date.now()) return true;
  }
  if (!isCurrentUser || !workspaceId) return false;
  if (!hasLocalCanvasCreateInFlight(workspaceId)) return false;
  pruneExpired(spentCanvasCreateEvents);
  spentCanvasCreateEvents.set(
    canvasId,
    Date.now() + LOCAL_CANVAS_CREATE_TTL_MS,
  );
  return true;
}

type CreateCanvasContext = { createWorkspaceId?: string };

export function useCreateCanvas(
  options?: UseMutationOptions<
    Canvas,
    AxiosError,
    CreateCanvasVariables,
    CreateCanvasContext
  >,
) {
  const queryClient = useQueryClient();
  const privateWorkspace = usePrivateWorkspace();

  return useMutation({
    mutationFn: ({ workspaceId, name, ...data }: CreateCanvasVariables) => {
      const targetId = workspaceId ?? privateWorkspace?.id;
      if (!targetId) {
        throw new Error("No workspace available to create canvas in");
      }
      return orgAPI.workspaces.createCanvas(targetId, {
        ...data,
        name: name?.trim() || DEFAULT_CANVAS_NAME,
      });
    },
    ...options,
    // Opened before the request and closed only on settle, so the window the
    // event can overtake the response in — including the gap before onSuccess
    // records the id — is covered end to end.
    onMutate: async (variables, ...rest) => {
      const fromOptions = await options?.onMutate?.(variables, ...rest);
      const createWorkspaceId = variables.workspaceId ?? privateWorkspace?.id;
      if (createWorkspaceId) beginLocalCanvasCreate(createWorkspaceId);
      return { ...fromOptions, createWorkspaceId };
    },
    onSettled: (data, error, variables, context, ...rest) => {
      if (context?.createWorkspaceId) {
        endLocalCanvasCreate(context.createWorkspaceId);
      }
      options?.onSettled?.(data, error, variables, context, ...rest);
    },
    onSuccess: (...args) => {
      const [data] = args;
      // Seed the detail entry before anything else: the canvas page's pending
      // gate, the tab registration, the tab strip title and the Liveblocks
      // room hold all read this key, and the create response comes from the
      // same converter the GET does — room id included.
      queryClient.setQueryData<Canvas>(["canvas", data.id], data);
      markCanvasCreatedLocally(data.id);
      insertCanvasIntoCachedLists(queryClient, data);
      // A map keyed by workspace, not a list — cheaper to refetch than splice.
      queryClient.invalidateQueries({
        queryKey: RECENT_CANVASES_BY_WORKSPACE_KEY,
      });
      // The tree row is a file record wrapping the canvas, with an id only the
      // server knows, so one tree fetch is the price of showing it.
      if (data.workspace_id) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(String(data.workspace_id)),
        });
      }
      options?.onSuccess?.(...args);
    },
  });
}

type UpdateCanvasVariables = { id: string; data: UpdateCanvasRequest };

type UpdateCanvasContext = { previousCanvas?: Canvas };

/**
 * Applies an update response to every cache the canvas appears in, and
 * refetches only the workspace trees whose contents actually changed. A tree
 * fetch presigns every row it returns, so a rename or a move earns one and a
 * metadata-only update does not.
 *
 * The tree row's `path` can embed the name, which the response doesn't carry,
 * so a rename refetches the one tree rather than guessing the new path.
 */
function applyCanvasUpdateToCaches(
  queryClient: QueryClient,
  canvas: Canvas,
  previousCanvas: Canvas | undefined,
  placementChanged: boolean,
): void {
  const canvasId = String(canvas.id);
  // Read before the patches below rewrite the very rows it reads.
  const placement = cachedCanvasPlacement(queryClient, canvasId);

  queryClient.setQueryData<Canvas>(["canvas", canvasId], canvas);
  patchCanvasInCachedLists(queryClient, canvas);
  patchCanvasInCachedTrees(queryClient, canvasId, (previous) => ({
    ...previous,
    ...canvas,
  }));
  rehomeCanvasInCachedWorkspaceLists(queryClient, canvas);
  queryClient.invalidateQueries({ queryKey: RECENT_CANVASES_BY_WORKSPACE_KEY });

  // Everything below decides which trees to refetch. A tree fetch presigns
  // every row it returns, so it is worth inferring; the list work above is
  // not, and no longer depends on this.
  const targetWorkspaceId = canvas.workspace_id
    ? String(canvas.workspace_id)
    : undefined;
  const sourceWorkspaceIds = new Set(placement.workspaceIds);
  if (previousCanvas?.workspace_id) {
    sourceWorkspaceIds.add(String(previousCanvas.workspace_id));
  }
  const movedWorkspace =
    targetWorkspaceId !== undefined &&
    [...sourceWorkspaceIds].some((id) => id !== targetWorkspaceId);
  // No cached row to compare against reads as changed: one fetch beats a rail
  // stuck on a stale path.
  const renamed = previousCanvas?.name !== canvas.name;

  if (!movedWorkspace && !placementChanged && !renamed) return;

  const trees = new Set(sourceWorkspaceIds);
  if (targetWorkspaceId) trees.add(targetWorkspaceId);
  for (const workspaceId of trees) {
    queryClient.invalidateQueries({
      queryKey: fileRecordKeys.workspace(workspaceId),
    });
  }
}

export function useUpdateCanvas(
  options?: UseMutationOptions<
    Canvas,
    AxiosError,
    UpdateCanvasVariables,
    UpdateCanvasContext
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: UpdateCanvasVariables) =>
      orgAPI.canvas.update(id, data),
    ...options,
    onMutate: async (variables, ...rest) => {
      // The source tree is only knowable from the row as it was before the
      // move; the response describes where the canvas landed.
      const previousCanvas = queryClient.getQueryData<Canvas>([
        "canvas",
        variables.id,
      ]);
      const fromOptions = await options?.onMutate?.(variables, ...rest);
      return { previousCanvas, ...fromOptions };
    },
    onSuccess: (data, variables, context, ...rest) => {
      applyCanvasUpdateToCaches(
        queryClient,
        data,
        context?.previousCanvas,
        variables.data.parent_path !== undefined,
      );
      options?.onSuccess?.(data, variables, context, ...rest);
    },
  });
}

export interface MoveCanvasInTreeParams {
  canvasId: string;
  /** Folder in the workspace tree ("" = root). */
  parentPath: string;
  /** Set to move the canvas to another workspace as well. */
  workspaceId?: string;
}

export type MoveCanvasInTree = (
  params: MoveCanvasInTreeParams,
) => Promise<Canvas>;

/**
 * The one way the client re-files a canvas: a single placement-only update.
 * Drag, Move to, bulk move and folder cascades all go through this.
 */
export function useMoveCanvasInTree(): MoveCanvasInTree {
  const updateCanvas = useUpdateCanvas({});
  return useCallback<MoveCanvasInTree>(
    ({ canvasId, parentPath, workspaceId }) =>
      updateCanvas.mutateAsync({
        id: canvasId,
        data: {
          parent_path: parentPath,
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
        },
      }),
    [updateCanvas],
  );
}

export function useDeleteCanvas(
  options?: UseMutationOptions<void, AxiosError, string>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => orgAPI.canvas.delete(id),
    ...options,
    onSuccess: (...args) => {
      const [, canvasId] = args;
      const trees = new Set(
        cachedCanvasPlacement(queryClient, canvasId).workspaceIds,
      );
      const cached = queryClient.getQueryData<Canvas>(["canvas", canvasId]);
      if (cached?.workspace_id) trees.add(String(cached.workspace_id));
      queryClient.removeQueries({ queryKey: ["canvas", canvasId] });
      removeCanvasFromCachedLists(queryClient, canvasId);
      for (const workspaceId of trees) {
        queryClient.invalidateQueries({
          queryKey: fileRecordKeys.workspace(workspaceId),
        });
      }
      // Deleting a canvas cascades to its chats and files on the backend, so
      // invalidate every cache that can contain trashed resources. That set
      // already spans the canvas and workspace lists.
      invalidateArchivedQueries(queryClient);
      options?.onSuccess?.(...args);
    },
  });
}

export function useCanvasFlowSessions(
  canvasId: string | undefined,
  options?: Partial<UseQueryOptions<FlowSession[], AxiosError>>,
) {
  return useQuery({
    queryKey: ["canvas-flow-sessions", canvasId],
    queryFn: () => {
      if (!canvasId) {
        throw new Error("Canvas ID is required");
      }
      return orgAPI.canvas.getFlowSessions(canvasId);
    },
    enabled: !!canvasId && (options?.enabled ?? true),
    ...options,
  });
}
